import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaSlidersH, FaMicrophone, FaPlug, FaBroadcastTower } from 'react-icons/fa';
import { GiSpeaker, GiAmplitude } from 'react-icons/gi';
import productCatalog from '../data/productCatalog.json';
import './Products.css';

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('speakers');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  const categories = [
    { id: 'speakers', name: 'Speakers', icon: <GiSpeaker /> },
    { id: 'amplifiers', name: 'Amplifiers', icon: <GiAmplitude /> },
    { id: 'microphones', name: 'Microphones', icon: <FaMicrophone /> },
    { id: 'mixers', name: "DSP's", icon: <FaBroadcastTower /> },
    { id: 'accessories', name: 'Cables & Connectors', icon: <FaPlug /> }
  ];

  useEffect(() => {
    filterProducts();
  }, [activeCategory, searchTerm]);

  const filterProducts = () => {
    let products = [];

    // Get products from selected category
    if (productCatalog[activeCategory]) {
      products = productCatalog[activeCategory].map(p => ({ ...p, category: activeCategory }));
    }

    // Apply search filter
    if (searchTerm) {
      products = products.filter(product =>
        product.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.type && product.type.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredProducts(products);
  };

  const getCategoryDisplayName = (category) => {
    const categoryMap = {
      'speakers': 'Speakers',
      'amplifiers': 'Amplifiers',
      'microphones': 'Microphones',
      'mixers': "DSP's",
      'accessories': 'Cables & Connectors'
    };
    return categoryMap[category] || category;
  };

  const getProductImage = (category) => {
    // Using different placeholder images for different categories
    const images = {
      'speakers': 'https://images.unsplash.com/photo-1545454660-877a4a8c4a9e?w=400&h=400&fit=crop&q=80',
      'amplifiers': 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=400&h=400&fit=crop&q=80',
      'microphones': 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=400&h=400&fit=crop&q=80',
      'mixers': 'https://images.unsplash.com/photo-1598653222000-6b7b7a552625?w=400&h=400&fit=crop&q=80',
      'accessories': 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=400&fit=crop&q=80'
    };
    return images[category] || images['speakers'];
  };

  return (
    <div className="products-page">
      {/* Hero Section */}
      <section className="products-hero">
        <div className="products-hero-overlay"></div>
        <div className="products-hero-content">
          <h1>Our Products</h1>
          <p>
            Explore our comprehensive range of professional audio equipment from world-class brands
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="products-content">
        <div className="container">
          {/* Search Bar */}
          <div className="search-section">
            <div className="search-bar">
              <FaSearch />
              <input
                type="text"
                placeholder="Search by brand, model, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Category Filters */}
          <div className="category-filters">
            {categories.map((category) => (
              <div
                key={category.id}
                className={`category-card ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategory(category.id);
                  setSearchTerm('');
                }}
              >
                <div className="category-icon">{category.icon}</div>
                <h3>{category.name}</h3>
              </div>
            ))}
          </div>

          {/* Products Grid */}
          <div className="products-results">
            <div className="results-header">
              <h2>
                {getCategoryDisplayName(activeCategory)}
              </h2>
              <p className="results-count">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} found
              </p>
            </div>

            {filteredProducts.length > 0 ? (
              <div className="products-grid">
                {filteredProducts.map((product) => (
                  <Link
                    to={`/product/${product.id}`}
                    key={product.id}
                    className="product-card"
                  >
                    <div className="product-image">
                      <img
                        src={getProductImage(product.category)}
                        alt={`${product.brand} ${product.model}`}
                      />
                      <span className="product-category-badge">
                        {getCategoryDisplayName(product.category)}
                      </span>
                    </div>
                    <div className="product-details">
                      <p className="product-brand">{product.brand}</p>
                      <h3 className="product-name">{product.model}</h3>
                      {product.type && (
                        <p className="product-type">{product.type}</p>
                      )}
                      <div className="product-footer">
                        <div className="product-price">
                          <span className="price">₹{product.price.toLocaleString('en-IN')}</span>
                          <span className="price-label">+ GST</span>
                        </div>
                        <button className="view-details-btn">View Details</button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="no-products">
                <FaSearch className="no-products-icon" />
                <h3>No Products Found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="products-cta">
        <div className="container">
          <h2>Need Help Choosing?</h2>
          <p>Our experts can help you select the perfect equipment for your needs</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">
              Get Expert Advice
            </Link>
            <a href="tel:+919876543210" className="btn btn-secondary">
              Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
